import { z } from 'zod';
import {
  commissionRulesSchema,
  DETAILED_SPARK_TYPES,
  percentIncreaseSchema,
  retailerSparkFiltersSchema,
  SPARK_FULFILLMENT_TYPES,
  SPARK_METRICS,
  SPARK_REQUEST_STATES,
  SPARK_TYPES,
  sparkAwardSchema,
  sparkCommissionSchema,
  sparkGoalSchema,
  sparkParticipantFiltersSchema,
  sparkParticipantGroupSchema,
  SparkDeclineResponse,
  SparkRequestState,
} from '../spark';
import { PAYMENT_METHOD_TYPES } from '../pos';
import { objectIdRegex } from '../util';

/**
 * Types for SparkBundle Retailers endpoint
 * Fetches list of retailers participating in a bundle
 */

export const getBundleRetailersPathParamsSchema = z.object({
  accountId: z.string().min(1), // Vendor account ID
  bundleId: z.string().min(1), // SparkBundle ID
});

/**
 * Path params for updating a bundled spark.
 */
export const updateBundledSparkPathParamsSchema = z.object({
  accountId: z.string().min(1),
  bundleId: z.string().min(1),
  sparkId: z.string().min(1),
});

export type UpdateBundledSparkPathParams = z.infer<typeof updateBundledSparkPathParamsSchema>;

/**
 * MasterSpark-level fields that propagate to the SparkBundle document and all
 * sibling sub-sparks when a bundled spark is edited.
 *
 * This is the single source of truth for which fields are considered "shared"
 * across a bundle. Adding a field here means it will be propagated to siblings.
 */
export const bundledSparkMasterUpdateSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  emoji: z.string().optional(),
  type: z.enum(SPARK_TYPES).optional(),
  metric: z.enum(SPARK_METRICS).optional(),
  detailedSparkType: z.enum(DETAILED_SPARK_TYPES).optional(),
  startDate: z.string().min(1).optional(),
  endDate: z.string().min(1).optional(),
  sparkBrandId: z.string().min(1).optional(),
  awards: z.array(sparkAwardSchema).optional(),
  goals: z.array(sparkGoalSchema).optional(),
  fulfillmentTypes: z.array(z.enum(SPARK_FULFILLMENT_TYPES)).optional(),
  splitPayout: z.boolean().optional(),
  minimumThresholdToQualify: z.number().optional(),
  minimumTransactionsToQualify: z.number().optional(),
  percentIncreaseData: percentIncreaseSchema.optional(),
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES).optional(),
  omitPreorders: z.boolean().optional(),

  // Future: commission fields (accepted but not specially handled yet)
  commissions: z.array(sparkCommissionSchema).optional(),
  commissionRules: commissionRulesSchema.optional().or(z.object({}).optional()),
});

export type BundledSparkMasterUpdate = z.infer<typeof bundledSparkMasterUpdateSchema>;

/**
 * Per-spark fields that only apply to the individual spark being edited.
 * These are NOT propagated to sibling sub-sparks or the SparkBundle document.
 *
 * Includes per-retailer fields (locationIds, retailerFilters, participants)
 * and operational fields (requestState, resentAt).
 */
export const bundledSparkPerSparkUpdateSchema = z.object({
  retailerFilters: retailerSparkFiltersSchema.partial().optional().or(z.object({}).optional()),
  locationIds: z.array(z.string().min(1)).optional(),
  posEmployeeProfileIds: z.array(z.string().min(1)).optional(),
  participantFilters: sparkParticipantFiltersSchema.optional(),
  requestState: z.enum(SPARK_REQUEST_STATES).optional(),
  resentAt: z.string().min(1).optional(),
  participantGroups: z.array(sparkParticipantGroupSchema).optional(),
});

export type BundledSparkPerSparkUpdate = z.infer<typeof bundledSparkPerSparkUpdateSchema>;

/**
 * Request body schema for updating a spark that belongs to a bundle.
 *
 * Merges the masterSpark and per-spark schemas into a single flat schema for the
 * request body. Uses default z.object() behavior to strip unknown keys so the UI
 * can send the full spark object unchanged.
 *
 * The controller parses the validated body through each sub-schema separately to
 * partition the fields before passing them to the service function.
 *
 * Explicitly omitted (stripped by Zod):
 *   groupId, recurringSchedule, courseData, trainingCourseId, snaps, vendorFilters
 */
export const updateBundledSparkSchema = bundledSparkMasterUpdateSchema
  .merge(bundledSparkPerSparkUpdateSchema)
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return new Date(data.startDate) <= new Date(data.endDate);
      }
      return true;
    },
    { message: 'startDate must not be after endDate' },
  );

export type UpdateBundledSparkRequestBody = z.infer<typeof updateBundledSparkSchema>;

export type GetBundleRetailersPathParams = z.infer<typeof getBundleRetailersPathParamsSchema>;

/**
 * Retailer summary for bundle retailers list view
 */
export interface BundleRetailer {
  /** Retailer account ID */
  id: string;
  /** Retailer name */
  name: string;
  /** Retailer logo URL (CloudFront CDN) */
  photo?: string;
  /** Request state for this retailer's spark in the bundle */
  requestState: SparkRequestState;
  /** Number of locations participating in this spark */
  locationCount: number;
  /** Number of employees participating in this spark */
  employeeCount: number;
  /** Number of qualifying products for this spark */
  qualifyingProductCount: number;
  /** The spark ID for this retailer in the bundle */
  sparkId: string;
  /** Decline response details (only present for rejected sparks) */
  declineResponse?: SparkDeclineResponse;
  /** Full name of the user who declined (only present for rejected sparks) */
  declinedByUser?: string;
}

export interface BundleRetailersResponseBody {
  data: BundleRetailer[];
}

/**
 * Recency window (days) used by the candidate-retailers query to determine
 * which retailers are "active sellers" for the bundle's brand. Retailers
 * whose latest BrandLink.lastTransactionDate is older than this window
 * (measured from the bundle's masterSpark.startDate) are filtered out.
 *
 * Centralized as a constant so the rule lives in one place across UI/API.
 */
export const MWL_CANDIDATE_RECENCY_WINDOW_DAYS = 60;

/**
 * Modes supported when retroactively adding a retailer to an MWL bundle.
 * - `create`: spark goes live with requestState='none' (no retailer approval needed).
 * - `request`: spark goes live with requestState='pending' and the retailer is notified.
 *   Only valid pre-startDate; server rejects with 400 if used post-startDate.
 */
export const ADD_RETAILER_TO_BUNDLE_MODES = ['create', 'request'] as const;
export type AddRetailerToBundleMode = typeof ADD_RETAILER_TO_BUNDLE_MODES[number];

/**
 * Path params for the candidate-retailers endpoint.
 */
export const getCandidateRetailersPathParamsSchema = z.object({
  accountId: z.string().min(1),
  bundleId: z.string().min(1),
});

export type GetCandidateRetailersPathParams = z.infer<typeof getCandidateRetailersPathParamsSchema>;

/**
 * Lightweight location summary returned per candidate retailer so the
 * locations-selection step doesn't need a follow-up fetch.
 */
export interface CandidateRetailerLocation {
  id: string;
  name: string;
  displayName?: string;
}

/**
 * A retailer eligible to be added to an MWL bundle.
 * Eligibility = has a BrandLink for the bundle's brand whose
 * lastTransactionDate is within MWL_CANDIDATE_RECENCY_WINDOW_DAYS of the
 * bundle's masterSpark.startDate, is in one of the bundle's markets, and is
 * not already a sub-spark in the bundle. Eligibility is fully evaluated
 * server-side; clients render whatever the endpoint returns.
 */
export interface CandidateRetailer {
  retailerAccountId: string;
  name: string;
  photo?: string;
  locations: CandidateRetailerLocation[];
}

export interface CandidateRetailersResponseBody {
  data: CandidateRetailer[];
}

/**
 * Path params for `POST /spark-bundles/:bundleId/retailers`.
 */
export const addRetailerToBundlePathParamsSchema = z.object({
  accountId: z.string().min(1),
  bundleId: z.string().min(1),
});

export type AddRetailerToBundlePathParams = z.infer<typeof addRetailerToBundlePathParamsSchema>;

/**
 * Request body for `POST /spark-bundles/:bundleId/retailers`.
 *
 * Mirrors `bulkRetailerSpecSchema` minus the commission fields (MWL sub-sparks
 * do not support commissions). `mode` is optional and defaults to `'create'`;
 * the server rejects with 400 if `'request'` is used after the bundle's
 * `masterSpark.startDate` has passed.
 */
export const addRetailerToBundleSchema = z.object({
  retailerAccountId: z.string().regex(objectIdRegex),
  locationIds: z.array(z.string().regex(objectIdRegex)).min(1),
  retailerFilters: retailerSparkFiltersSchema,
  mode: z.enum(ADD_RETAILER_TO_BUNDLE_MODES).optional().default('create'),
});

export type AddRetailerToBundleRequestBody = z.infer<typeof addRetailerToBundleSchema>;

export interface AddRetailerToBundleResponseBody {
  ok: true;
  sparkId: string;
  bundleId: string;
}
